/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { forwardRef, type ReactNode, useCallback, useEffect, useRef } from 'react';

import { css, jsx } from '@compiled/react';

import { announce } from '@atlaskit/pragmatic-drag-and-drop-live-region';
import { token } from '@atlaskit/tokens';

const activeStyle = css({
	backgroundColor: token('color.background.information'),
	borderRadius: token('radius.xsmall'),
});

const enteredStyle = css({
	outlineOffset: token('space.075'),
	outlineWidth: token('border.width.selected'),
	outlineStyle: 'solid',
	outlineColor: token('color.border.selected'),
});

export type GroupDropIndicatorWithDragOverProps = {
	ariaLabel?: string;
	children: ReactNode;
	disableEntered?: boolean;
	isActive: boolean;
	testId?: string;
};

export const GroupDropIndicatorWithDragOver = forwardRef<
	HTMLDivElement,
	GroupDropIndicatorWithDragOverProps
>(function GroupDropIndicatorWithDragOver(
	{ children, isActive, testId, disableEntered, ariaLabel },
	forwardedRef,
) {
	const [isEntered, setIsEntered] = React.useState(false);
	const ref = useRef<HTMLDivElement>(null);

	const handleDragEnter = useCallback(() => {
		if (!isActive || Boolean(disableEntered) || isEntered) {
			return;
		}
		setIsEntered(true);
	}, [disableEntered, isActive, isEntered]);

	const handleDragLeave = useCallback(
		(event: React.DragEvent<HTMLDivElement>) => {
			if (!isActive || Boolean(disableEntered) || !isEntered) {
				return;
			}
			const relatedTarget = event.relatedTarget as HTMLElement;

			if (ref.current && relatedTarget && ref.current.contains(relatedTarget)) {
				return; // Still within bounds, don't trigger leave
			}
			setIsEntered(false);
		},
		[disableEntered, isActive, isEntered],
	);

	// Resets entered state if isActive changes to false
	useEffect(() => {
		if (!isActive) {
			setIsEntered(false);
		}
	}, [isActive]);

	// Announcer
	useEffect(() => {
		if (isEntered && ariaLabel) {
			announce(ariaLabel);
		}
	}, [ariaLabel, isEntered]);

	return (
		<div ref={ref}>
			<div
				onDragEnter={handleDragEnter} // eslint-disable-line @atlaskit/design-system/no-direct-use-of-web-platform-drag-and-drop
				onDragLeave={handleDragLeave} // eslint-disable-line @atlaskit/design-system/no-direct-use-of-web-platform-drag-and-drop
				ref={forwardedRef}
				data-testid={testId}
				data-is-active={isActive}
				data-is-entered={isEntered}
				css={[isActive && activeStyle, isEntered && enteredStyle]}
				role={isActive ? 'region' : undefined}
				aria-label={isActive && ariaLabel ? ariaLabel : undefined}
			>
				{children}
			</div>
		</div>
	);
});
